import { Input, Checkbox, Textarea } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { RichTextInput } from '@/components/form/RichTextInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { MediaGalleryPicker } from '@/components/media/MediaGalleryPicker.js';
import {
  ColumnsPicker,
  EnumSelect,
  FieldGroup,
  IconInput,
  Repeater,
  emptyTranslatable,
} from '../BlockFields.js';
import { newBlockId } from '../block-catalog.js';
import type { BlockFormProps } from './types.js';

// ─── 1. Bosh slayder ─────────────────────────────────────────────────────────

export function HeroSliderForm({ data, onChange }: BlockFormProps<'hero-slider'>) {
  return (
    <>
      <Repeater
        label="Slaydlar"
        max={12}
        addLabel="Slayd qo‘shish"
        items={data.slides}
        getId={(slide) => slide._id}
        summary={(slide, index) => slide.title.uz || `${index + 1}-slayd`}
        create={() => ({
          _id: newBlockId(),
          image: null,
          kicker: emptyTranslatable(),
          title: emptyTranslatable(),
          body: emptyTranslatable(),
          cta: { label: emptyTranslatable(), href: '' },
          cta2: { label: emptyTranslatable(), href: '' },
          textAlign: 'left' as const,
          isVisible: true,
          order: data.slides.length,
        })}
        onChange={(slides) =>
          onChange({ ...data, slides: slides.map((s, i) => ({ ...s, order: i })) })
        }
        renderItem={(slide, update) => (
          <>
            <MediaPicker
              label="Rasm"
              value={slide.image}
              onChange={(image) => update({ ...slide, image })}
              hint="Kengligi kamida 1920px — slayder butun ekranni egallaydi"
            />
            <TranslatableInput
              label="Kichik sarlavha"
              value={slide.kicker}
              onChange={(kicker) => update({ ...slide, kicker })}
            />
            <TranslatableInput
              label="Sarlavha"
              value={slide.title}
              onChange={(title) => update({ ...slide, title })}
            />
            <TranslatableInput
              label="Matn"
              multiline
              rows={3}
              value={slide.body}
              onChange={(body) => update({ ...slide, body })}
            />
            <TranslatableInput
              label="Asosiy tugma matni"
              value={slide.cta.label}
              onChange={(label) => update({ ...slide, cta: { ...slide.cta, label } })}
            />
            <Input
              label="Asosiy tugma havolasi"
              value={slide.cta.href}
              placeholder="/xizmatlar"
              onChange={(event) =>
                update({ ...slide, cta: { ...slide.cta, href: event.target.value } })
              }
            />
            {/*
              Ikkinchi tugma — ixtiyoriy. Matni yoki havolasi bo'sh
              bo'lsa saytda umuman chizilmaydi.
            */}
            <TranslatableInput
              label="Ikkinchi tugma matni"
              value={slide.cta2.label}
              onChange={(label) => update({ ...slide, cta2: { ...slide.cta2, label } })}
            />
            <Input
              label="Ikkinchi tugma havolasi"
              value={slide.cta2.href}
              placeholder="/markaz-haqida"
              hint="Bo‘sh qoldirilsa ikkinchi tugma ko‘rsatilmaydi"
              onChange={(event) =>
                update({ ...slide, cta2: { ...slide.cta2, href: event.target.value } })
              }
            />
            <EnumSelect
              label="Matn joylashuvi"
              value={slide.textAlign}
              options={[
                { value: 'left', label: 'Chapda' },
                { value: 'center', label: 'Markazda' },
              ]}
              onChange={(textAlign) => update({ ...slide, textAlign })}
            />
            <Checkbox
              label="Slayd ko‘rsatilsin"
              checked={slide.isVisible}
              onChange={(event) => update({ ...slide, isVisible: event.target.checked })}
            />
          </>
        )}
      />

      <FieldGroup title="Slayder sozlamalari">
        <Checkbox
          label="Avtomatik aylanish"
          checked={data.autoplay}
          onChange={(event) => onChange({ ...data, autoplay: event.target.checked })}
        />
        {data.autoplay && (
          <Input
            type="number"
            label="Almashish oralig‘i (millisoniya)"
            min={2000}
            max={20000}
            step={500}
            value={data.intervalMs}
            onChange={(event) => onChange({ ...data, intervalMs: Number(event.target.value) })}
          />
        )}
        <EnumSelect
          label="Balandlik"
          value={data.height}
          options={[
            { value: 'md', label: 'O‘rtacha' },
            { value: 'lg', label: 'Katta' },
            { value: 'full', label: 'Butun ekran' },
          ]}
          onChange={(height) => onChange({ ...data, height })}
        />
      </FieldGroup>
    </>
  );
}

// ─── 2. Sahifa banneri ───────────────────────────────────────────────────────

export function PageHeaderForm({ data, onChange }: BlockFormProps<'page-header'>) {
  return (
    <>
      <MediaPicker
        label="Fon rasmi"
        value={data.image}
        onChange={(image) => onChange({ ...data, image })}
      />
      <TranslatableInput
        label="Kichik sarlavha"
        value={data.kicker}
        onChange={(kicker) => onChange({ ...data, kicker })}
      />
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <TranslatableInput
        label="Tavsif"
        multiline
        rows={2}
        value={data.subtitle}
        onChange={(subtitle) => onChange({ ...data, subtitle })}
      />
      <Checkbox
        label="«Bosh sahifa / …» yo‘lchisi ko‘rsatilsin"
        checked={data.showBreadcrumbs}
        onChange={(event) => onChange({ ...data, showBreadcrumbs: event.target.checked })}
      />
    </>
  );
}

// ─── 3. Matn ─────────────────────────────────────────────────────────────────

export function RichTextForm({ data, onChange }: BlockFormProps<'rich-text'>) {
  return (
    <>
      <RichTextInput
        label="Matn"
        value={data.content}
        onChange={(content) => onChange({ ...data, content })}
      />
      <EnumSelect
        label="Ustunlar"
        value={String(data.columns) as '1' | '2'}
        options={[
          { value: '1', label: 'Bitta ustun' },
          { value: '2', label: 'Ikki ustun' },
        ]}
        onChange={(value) => onChange({ ...data, columns: value === '2' ? 2 : 1 })}
      />
    </>
  );
}

// ─── 4. Matn + rasm ──────────────────────────────────────────────────────────

export function TextImageForm({ data, onChange }: BlockFormProps<'text-image'>) {
  return (
    <>
      <RichTextInput
        label="Matn"
        value={data.content}
        onChange={(content) => onChange({ ...data, content })}
      />
      <MediaPicker
        label="Rasm"
        value={data.image}
        onChange={(image) => onChange({ ...data, image })}
      />
      <EnumSelect
        label="Rasm joylashuvi"
        value={data.imagePosition}
        options={[
          { value: 'left', label: 'Chapda' },
          { value: 'right', label: 'O‘ngda' },
        ]}
        onChange={(imagePosition) => onChange({ ...data, imagePosition })}
      />
      <EnumSelect
        label="Rasm kengligi"
        value={String(data.imageWidth) as '33' | '50'}
        options={[
          { value: '33', label: 'Uchdan bir' },
          { value: '50', label: 'Yarim' },
        ]}
        onChange={(value) => onChange({ ...data, imageWidth: value === '33' ? 33 : 50 })}
      />
      <TranslatableInput
        label="Tugma matni"
        value={data.cta.label}
        onChange={(label) => onChange({ ...data, cta: { ...data.cta, label } })}
      />
      <Input
        label="Tugma havolasi"
        value={data.cta.href}
        onChange={(event) => onChange({ ...data, cta: { ...data.cta, href: event.target.value } })}
      />
    </>
  );
}

// ─── 5. Raqamlar ─────────────────────────────────────────────────────────────

export function StatsForm({ data, onChange }: BlockFormProps<'stats'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <Repeater
        label="Ko‘rsatkichlar"
        max={8}
        items={data.items}
        getId={(_, index) => `stat-${index}`}
        summary={(item, index) => `${item.value || '—'} ${item.label.uz}`.trim() || `${index + 1}`}
        create={() => ({
          value: '',
          prefix: '',
          suffix: '',
          label: emptyTranslatable(),
          icon: '',
        })}
        onChange={(items) => onChange({ ...data, items })}
        renderItem={(item, update) => (
          <>
            <div className="grid grid-cols-3 gap-2">
              <Input
                label="Oldidan"
                value={item.prefix}
                placeholder="+"
                onChange={(event) => update({ ...item, prefix: event.target.value })}
              />
              <Input
                label="Qiymat"
                value={item.value}
                placeholder="120"
                onChange={(event) => update({ ...item, value: event.target.value })}
              />
              <Input
                label="Keyin"
                value={item.suffix}
                placeholder="+"
                onChange={(event) => update({ ...item, suffix: event.target.value })}
              />
            </div>
            <TranslatableInput
              label="Izoh"
              value={item.label}
              onChange={(label) => update({ ...item, label })}
            />
            <IconInput value={item.icon} onChange={(icon) => update({ ...item, icon })} />
          </>
        )}
      />
      <Checkbox
        label="Raqamlar sanab ko‘rsatilsin (animatsiya)"
        checked={data.animate}
        onChange={(event) => onChange({ ...data, animate: event.target.checked })}
      />
    </>
  );
}

// ─── 6. Ustunliklar ──────────────────────────────────────────────────────────

export function FeaturesForm({ data, onChange }: BlockFormProps<'features'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <Repeater
        label="Kartochkalar"
        max={12}
        items={data.items}
        getId={(_, index) => `feature-${index}`}
        summary={(item, index) => item.title.uz || `${index + 1}-kartochka`}
        create={() => ({ icon: '', title: emptyTranslatable(), text: emptyTranslatable() })}
        onChange={(items) => onChange({ ...data, items })}
        renderItem={(item, update) => (
          <>
            <IconInput value={item.icon} onChange={(icon) => update({ ...item, icon })} />
            <TranslatableInput
              label="Sarlavha"
              value={item.title}
              onChange={(title) => update({ ...item, title })}
            />
            <TranslatableInput
              label="Matn"
              multiline
              rows={3}
              value={item.text}
              onChange={(text) => update({ ...item, text })}
            />
          </>
        )}
      />
      {/* 1 — kataksiz, raqamlangan ro‘yxat («faoliyat yo‘nalishlari») */}
      <ColumnsPicker
        value={data.columns}
        options={[1, 2, 3, 4]}
        onChange={(columns) => onChange({ ...data, columns: columns as 1 | 2 | 3 | 4 })}
      />
    </>
  );
}

// ─── 7. Galereya ─────────────────────────────────────────────────────────────

export function GalleryForm({ data, onChange }: BlockFormProps<'gallery'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <MediaGalleryPicker
        label="Rasmlar"
        value={data.images}
        onChange={(images) => onChange({ ...data, images })}
      />
      <ColumnsPicker
        value={data.columns}
        options={[2, 3, 4]}
        onChange={(columns) => onChange({ ...data, columns: columns as 2 | 3 | 4 })}
      />
      <Checkbox
        label="Bosilganda kattalashsin"
        checked={data.enableLightbox}
        onChange={(event) => onChange({ ...data, enableLightbox: event.target.checked })}
      />
    </>
  );
}

// ─── 8. Video ────────────────────────────────────────────────────────────────

export function VideoForm({ data, onChange }: BlockFormProps<'video'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <EnumSelect
        label="Manba"
        value={data.source}
        options={[
          { value: 'youtube', label: 'YouTube havolasi' },
          { value: 'upload', label: 'Yuklangan fayl' },
        ]}
        onChange={(source) => onChange({ ...data, source })}
      />
      {data.source === 'youtube' ? (
        <Input
          label="YouTube havolasi"
          value={data.youtubeUrl}
          placeholder="https://www.youtube.com/watch?v=…"
          onChange={(event) => onChange({ ...data, youtubeUrl: event.target.value })}
        />
      ) : (
        <MediaPicker
          label="Video fayl"
          kind="file"
          value={data.videoFile}
          onChange={(videoFile) => onChange({ ...data, videoFile })}
        />
      )}
      <MediaPicker
        label="Muqova rasmi"
        value={data.poster}
        onChange={(poster) => onChange({ ...data, poster })}
        hint="Video yuklanguncha ko‘rinadi"
      />
    </>
  );
}

// ─── 9. Savol-javob ──────────────────────────────────────────────────────────

export function FaqForm({ data, onChange }: BlockFormProps<'faq'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <Repeater
        label="Savollar"
        items={data.items}
        getId={(_, index) => `faq-${index}`}
        summary={(item, index) => item.question.uz || `${index + 1}-savol`}
        create={() => ({ question: emptyTranslatable(), answer: emptyTranslatable() })}
        onChange={(items) => onChange({ ...data, items })}
        renderItem={(item, update) => (
          <>
            <TranslatableInput
              label="Savol"
              value={item.question}
              onChange={(question) => update({ ...item, question })}
            />
            <TranslatableInput
              label="Javob"
              multiline
              rows={4}
              value={item.answer}
              onChange={(answer) => update({ ...item, answer })}
            />
          </>
        )}
      />
    </>
  );
}

// ─── 10. Akkordeon ───────────────────────────────────────────────────────────

export function AccordionForm({ data, onChange }: BlockFormProps<'accordion'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <Repeater
        label="Bo‘limlar"
        items={data.items}
        getId={(_, index) => `acc-${index}`}
        summary={(item, index) => item.title.uz || `${index + 1}-bo‘lim`}
        create={() => ({ title: emptyTranslatable(), content: emptyTranslatable() })}
        onChange={(items) => onChange({ ...data, items })}
        renderItem={(item, update) => (
          <>
            <TranslatableInput
              label="Sarlavha"
              value={item.title}
              onChange={(title) => update({ ...item, title })}
            />
            <RichTextInput
              label="Tarkib"
              value={item.content}
              onChange={(content) => update({ ...item, content })}
            />
          </>
        )}
      />
    </>
  );
}

// ─── 11. Xronologiya ─────────────────────────────────────────────────────────

export function TimelineForm({ data, onChange }: BlockFormProps<'timeline'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <Repeater
        label="Bosqichlar"
        items={data.items}
        getId={(_, index) => `tl-${index}`}
        summary={(item, index) => `${item.year} ${item.title.uz}`.trim() || `${index + 1}`}
        create={() => ({
          year: '',
          title: emptyTranslatable(),
          text: emptyTranslatable(),
          image: null,
        })}
        onChange={(items) => onChange({ ...data, items })}
        renderItem={(item, update) => (
          <>
            <Input
              label="Yil"
              value={item.year}
              placeholder="2019"
              onChange={(event) => update({ ...item, year: event.target.value })}
            />
            <TranslatableInput
              label="Sarlavha"
              value={item.title}
              onChange={(title) => update({ ...item, title })}
            />
            <TranslatableInput
              label="Matn"
              multiline
              rows={3}
              value={item.text}
              onChange={(text) => update({ ...item, text })}
            />
            <MediaPicker
              label="Rasm"
              value={item.image}
              onChange={(image) => update({ ...item, image })}
            />
          </>
        )}
      />
    </>
  );
}

// ─── 12. Hamkorlar ───────────────────────────────────────────────────────────

export function PartnersForm({ data, onChange }: BlockFormProps<'partners'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <Repeater
        label="Logotiplar"
        items={data.logos}
        getId={(_, index) => `logo-${index}`}
        summary={(item, index) => item.name || `${index + 1}-logotip`}
        create={() => ({ image: null, name: '', url: '' })}
        onChange={(logos) => onChange({ ...data, logos })}
        renderItem={(item, update) => (
          <>
            <MediaPicker
              label="Logotip"
              value={item.image}
              onChange={(image) => update({ ...item, image })}
            />
            <Input
              label="Nomi"
              value={item.name}
              onChange={(event) => update({ ...item, name: event.target.value })}
            />
            <Input
              label="Havola"
              value={item.url}
              placeholder="https://…"
              onChange={(event) => update({ ...item, url: event.target.value })}
            />
          </>
        )}
      />
      <Checkbox
        label="Aylanuvchi qator (karusel)"
        checked={data.isCarousel}
        onChange={(event) => onChange({ ...data, isCarousel: event.target.checked })}
      />
      <Checkbox
        label="Kulrang qilib ko‘rsatilsin"
        checked={data.grayscale}
        onChange={(event) => onChange({ ...data, grayscale: event.target.checked })}
      />
    </>
  );
}

// ─── 13. Chaqiriq ────────────────────────────────────────────────────────────

export function CtaForm({ data, onChange }: BlockFormProps<'cta'>) {
  return (
    <>
      <TranslatableInput
        label="Sarlavha"
        value={data.title}
        onChange={(title) => onChange({ ...data, title })}
      />
      <TranslatableInput
        label="Matn"
        multiline
        rows={3}
        value={data.text}
        onChange={(text) => onChange({ ...data, text })}
      />
      <TranslatableInput
        label="Tugma matni"
        value={data.cta.label}
        onChange={(label) => onChange({ ...data, cta: { ...data.cta, label } })}
      />
      <Input
        label="Tugma havolasi"
        value={data.cta.href}
        onChange={(event) => onChange({ ...data, cta: { ...data.cta, href: event.target.value } })}
      />
      <EnumSelect
        label="Uslub"
        value={data.style}
        options={[
          { value: 'primary', label: 'Asosiy rang' },
          { value: 'dark', label: 'To‘q' },
          { value: 'accent', label: 'Aksent' },
        ]}
        onChange={(style) => onChange({ ...data, style })}
      />
    </>
  );
}

// ─── 14. Xarita ──────────────────────────────────────────────────────────────

export function MapForm({ data, onChange }: BlockFormProps<'map'>) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="number"
          label="Kenglik (lat)"
          step="0.000001"
          value={data.lat ?? ''}
          placeholder="41.311081"
          onChange={(event) =>
            onChange({
              ...data,
              lat: event.target.value === '' ? null : Number(event.target.value),
            })
          }
        />
        <Input
          type="number"
          label="Uzunlik (lng)"
          step="0.000001"
          value={data.lng ?? ''}
          placeholder="69.240562"
          onChange={(event) =>
            onChange({
              ...data,
              lng: event.target.value === '' ? null : Number(event.target.value),
            })
          }
        />
      </div>
      <Input
        type="number"
        label="Masshtab"
        min={1}
        max={20}
        value={data.zoom}
        onChange={(event) => onChange({ ...data, zoom: Number(event.target.value) })}
      />
      <Input
        type="number"
        label="Balandlik (px)"
        min={200}
        max={900}
        step={20}
        value={data.height}
        onChange={(event) => onChange({ ...data, height: Number(event.target.value) })}
      />
      <TranslatableInput
        label="Belgi matni"
        value={data.markerTitle}
        onChange={(markerTitle) => onChange({ ...data, markerTitle })}
      />
      <Checkbox
        label="«Yo‘nalish qurish» tugmasi"
        checked={data.showRouteButton}
        onChange={(event) => onChange({ ...data, showRouteButton: event.target.checked })}
      />
    </>
  );
}

// ─── 15. HTML ────────────────────────────────────────────────────────────────

export function HtmlForm({ data, onChange }: BlockFormProps<'html'>) {
  return (
    <Textarea
      label="HTML kod"
      rows={12}
      value={data.code}
      onChange={(event) => onChange({ ...data, code: event.target.value })}
      className="font-mono text-xs"
      hint="Faqat ishonchli kod joylang. Kod saytga o‘zgarishsiz tushadi va sahifani buzishi mumkin"
    />
  );
}
